/**
 * Detalhe da solicitação em SlideOverPanel — cabeçalho, peças (com fotos e
 * zoom), itens, histórico e ações por estado (autorizar/recusar/atender/
 * cancelar). Recusa exige motivo; cancelamento tem motivo opcional.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiCheckCircle, FiEdit2, FiImage, FiPrinter, FiTrash2 } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import ImagemViewer from "../../../components/ImagemViewer";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import {
  SITUACAO_LABELS,
  SITUACAO_TONES,
  atenderSolicitacao,
  soltarReservasPresas,
  cancelarSolicitacao,
  editarFotoSolicitacao,
  excluirSolicitacao,
  iniciarSolicitacao,
  marcarProntaSolicitacao,
  obterAssistencia,
  obterRequisicao,
  obterRetirada,
  podeAcaoTipo,
  recusarSolicitacao,
  restaurarFotoSolicitacao,
  separarItemSolicitacao,
  separarSolicitacao,
} from "../../../services/solicitacoes";
import AutorizarClassificarModal from "./AutorizarClassificarModal";
import ClassificacaoResumo from "./ClassificacaoResumo";
import PrazoPanel from "./PrazoPanel";
import RetiradaModal from "./RetiradaModal";
import AvisoPerdaProducao, { etapasDoErro } from "./AvisoPerdaProducao";
import Conversa from "../_shared/ConversaSolicitacao";
import ConfirmDialog from "../../../components/ConfirmDialog";
import { imprimirComprovanteRetirada } from "./imprimirRetirada";
import { imprimirComprovanteSeparacao } from "./imprimirSeparacao";
import {
  cabecalhoDaFoto,
  imprimirImagens,
  paginasDaSolicitacao,
} from "../Solicitacoes/imprimirImagens";
import { SectionTitle } from "../../../styles/shared";
import { formatarReaisBR } from "../../../utils/dinheiro";
import { corDaFita } from "../Solicitacoes/utils";
import { fmtQtd } from "../helpers";
import CarrosselPecasFita from "./CarrosselPecasFita";
import { TIPO_LABELS, dataHora, formatMedidas } from "./helpers";
import {
  BtnImagens,
  DangerGhostBtn,
  DetailGrid,
  DetailItem,
  DetailSection,
  DetailTopRow,
  ErrorMsg,
  FitaDot,
  FitaWrap,
  FotoThumb,
  FotosRow,
  GhostBtn,
  InlineError,
  Label,
  LoteTag,
  MotivoBox,
  NeutralBanner,
  PecaCard,
  PecaHead,
  PecaLinha,
  Pill,
  PrimaryBtn,
  RowEnd,
  TextArea,
  Timeline,
  TimelineItem,
  WarningBanner,
} from "./styles";
import { Carregando } from "../_shared/Estados";

/**
 * Troca UMA foto no detalhe já carregado, sem rebaixar o payload inteiro.
 * O detalhe traz todas as fotos em base64; recarregar a cada giro custava
 * centenas de KB no 3G da obra e ainda fazia o usuário esperar para ver o
 * resultado do próprio giro.
 */
function trocarFotoNoDetalhe(detalhe, fotoId, imagem, editada) {
  if (!detalhe) return detalhe;
  const trocar = (fotos) =>
    (fotos || []).map((f) => (f.id === fotoId ? { ...f, imagem, editada } : f));
  return {
    ...detalhe,
    fotos: detalhe.fotos ? trocar(detalhe.fotos) : detalhe.fotos,
    pecas: detalhe.pecas
      ? detalhe.pecas.map((p) => ({ ...p, fotos: trocar(p.fotos) }))
      : detalhe.pecas,
  };
}

const NATUREZA = {
  NOVA: { label: "Peça nova", tone: "info" },
  MAO_DE_OBRA: { label: "Mão de obra", tone: "neutral" },
};

function Fitas({ peca }) {
  const alguma = peca.fita_frente || peca.fita_traseira || peca.fita_le || peca.fita_ld;
  if (!alguma && !peca.fita_nome) return null;
  return (
    <FitaWrap>
      <span>Fitas:</span>
      <FitaDot $on={!!peca.fita_frente} title="Frente">F</FitaDot>
      <FitaDot $on={!!peca.fita_traseira} title="Traseira">T</FitaDot>
      <FitaDot $on={!!peca.fita_le} title="Lado esquerdo">LE</FitaDot>
      <FitaDot $on={!!peca.fita_ld} title="Lado direito">LD</FitaDot>
      {peca.fita_nome && <span>{corDaFita(peca.fita_nome)}</span>}
    </FitaWrap>
  );
}

// `peca` viaja junto para o visualizador montar o cabeçalho de impressão
// (descrição do item). Em RQA/RQI a foto é da solicitação e vem sem peça.
function GaleriaFotos({ fotos, peca = null, onAbrir }) {
  if (!fotos?.length) return null;
  return (
    <FotosRow>
      {fotos.map((f) => (
        <FotoThumb
          key={f.id}
          src={f.imagem}
          alt={f.legenda || "Foto da solicitação"}
          title={f.legenda || ""}
          onClick={() => onAbrir({ foto: f, peca })}
        />
      ))}
    </FotosRow>
  );
}

/** Número clicável que troca o detalhe aberto (junção). */
function LinkSolicitacao({ numero, onAbrir }) {
  return (
    <button
      type="button"
      className="mono"
      onClick={onAbrir}
      style={{
        background: "none", border: 0, padding: 0, color: "var(--accent)",
        textDecoration: "underline", cursor: "pointer", font: "inherit",
      }}
    >
      {numero}
    </button>
  );
}

export default function DetalheSlideOver({
  familia,
  id,
  numero,
  onClose,
  onChanged,
  onAbrirOutra,
}) {
  const { request, hasPermission } = useAuth();
  const toast = useToast();
  const podeExcluir = hasPermission("solicitacao:delete");

  const [dado, setDado] = useState(null);
  const [erro, setErro] = useState(null);
  const [modoMotivo, setModoMotivo] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [erroMotivo, setErroMotivo] = useState("");
  // Cancelar com trabalho feito na fábrica: etapas vindas do backend + aceite.
  const [perda, setPerda] = useState(null);
  const [perdaConfirmada, setPerdaConfirmada] = useState(false);
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
  const [fotoAberta, setFotoAberta] = useState(null);
  // "autorizar" | "classificar" — abre o modal de classificação por item.
  const [classificar, setClassificar] = useState(null);
  const [retiradaAberta, setRetiradaAberta] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const detalhe =
        familia === "requisicao"
          ? await obterRequisicao(request, id)
          : await obterAssistencia(request, id);
      setDado(detalhe);
      setErro(null);
    } catch (e) {
      setErro(e?.message || "Erro ao carregar a solicitação.");
    }
  }, [request, familia, id]);

  useEffect(() => {
    setDado(null);
    setErro(null);
    setModoMotivo(null);
    setMotivo("");
    setErroMotivo("");
    carregar();
  }, [carregar]);

  const aposAcao = useCallback(async () => {
    await carregar();
    onChanged?.();
  }, [carregar, onChanged]);

  // Girar/cortar salvam sozinhos. Recarrega o detalhe para o estado da tela
  // (inclusive o marcador `editada`) vir do servidor, e nao de um palpite
  // local — se o POST falhar, o visualizador mantem a imagem antiga.
  // Aplica a foto nova no estado local (detalhe + a que está aberta) em vez de
  // recarregar tudo: a resposta do POST já diz o que precisamos saber.
  const aplicarFoto = (fotoId, imagem, editada) => {
    setDado((d) => trocarFotoNoDetalhe(d, fotoId, imagem, editada));
    setFotoAberta((atual) =>
      atual && atual.foto.id === fotoId
        ? { ...atual, foto: { ...atual.foto, imagem, editada } }
        : atual);
    onChanged?.();
  };

  const salvarFoto = async (imagem) => {
    const r = await editarFotoSolicitacao(
      request, familia, id, fotoAberta.foto.id, imagem,
    );
    aplicarFoto(fotoAberta.foto.id, imagem, r?.editada ?? true);
  };

  const restaurarFoto = async () => {
    const r = await restaurarFotoSolicitacao(
      request, familia, id, fotoAberta.foto.id,
    );
    if (r?.imagem) aplicarFoto(fotoAberta.foto.id, r.imagem, false);
    return r?.imagem;
  };

  const confirmarMotivo = async () => {
    const texto = motivo.trim();
    if (modoMotivo === "recusar" && !texto) {
      setErroMotivo("Informe o motivo da recusa.");
      return;
    }
    setErroMotivo("");
    if (modoMotivo === "recusar") {
      await recusarSolicitacao(request, familia, id, texto);
      toast.success("Solicitação recusada.");
    } else {
      if (perda && !perdaConfirmada) {
        setErroMotivo("Marque a confirmação da perda para cancelar.");
        return;
      }
      try {
        await cancelarSolicitacao(request, familia, id, texto, { confirmarPerda: perdaConfirmada });
      } catch (e) {
        const etapas = etapasDoErro(e);
        if (!etapas) throw e;
        setPerda(etapas);
        return;
      }
      toast.success("Solicitação cancelada.");
    }
    setModoMotivo(null);
    setMotivo("");
    setPerda(null);
    setPerdaConfirmada(false);
    await aposAcao();
  };

  // Exclusão DEFINITIVA (hard delete) — só quem tem solicitacao:delete.
  // Diferente de cancelar (soft, mantém histórico).
  const excluir = async () => {
    setConfirmandoExclusao(true);
  };
  const excluirDeVez = async () => {
    await excluirSolicitacao(request, familia, id);
    setConfirmandoExclusao(false);
    toast.success(`Solicitação ${numero} excluída.`);
    onChanged?.();
    onClose?.();
  };

  // Marca a requisição como separada (grava quem+quando na 1ª vez) e abre o
  // comprovante de separação pra imprimir e colar na caixa.
  const separarEImprimir = async () => {
    let atual = dado;
    if (!dado?.separado_em) {
      await separarSolicitacao(request, id);
      atual = await obterRequisicao(request, id);
      setDado(atual);
      onChanged?.();
    }
    if (!imprimirComprovanteSeparacao(atual)) {
      toast.error(
        "Não foi possível abrir a impressão (pop-up bloqueado?). Libere pop-ups e tente de novo.",
      );
    }
  };

  // Reimprime o comprovante de uma retirada (busca a assinatura, que a
  // lista não traz para ficar leve).
  const reimprimirRetirada = async (r) => {
    const completa = await obterRetirada(request, familia, id, r.id);
    if (!imprimirComprovanteRetirada(dado, completa)) {
      toast.error("Não foi possível abrir a impressão (pop-up bloqueado?). Libere pop-ups e tente de novo.");
    }
  };

  // Fotos da solicitação já com cabeçalho — alimenta o botão do topo. Refaz
  // quando `dado` muda (inclusive depois de girar/cortar, que troca a imagem).
  const paginasImagens = useMemo(() => paginasDaSolicitacao(dado), [dado]);

  const imprimirTodasAsImagens = () => {
    if (!paginasImagens.length) return;
    if (!imprimirImagens(paginasImagens)) {
      toast.error(
        "Não foi possível abrir a impressão (pop-up bloqueado?). " +
        "Libere pop-ups e tente de novo.",
      );
    }
  };

  const situacao = dado?.situacao;
  const juntadaMeta = [dado?.juntada_por_nome, dado?.juntada_em && dataHora(dado.juntada_em)]
    .filter(Boolean).join(" · ");
  const terminal = ["RECUSADA", "CANCELADA", "ATENDIDA", "JUNTADA"].includes(situacao);
  // Ações gated pelo TIPO da solicitação carregada (RBAC granular).
  const podeAutorizar = podeAcaoTipo(hasPermission, "autorizar", dado?.tipo);
  const podeAtender = podeAcaoTipo(hasPermission, "atender", dado?.tipo);

  let footer = null;
  if (dado && !terminal) {
    if (modoMotivo) {
      footer = (
        <MotivoBox>
          <Label>
            {modoMotivo === "recusar"
              ? "Motivo da recusa (obrigatório)"
              : "Motivo do cancelamento (opcional)"}
          </Label>
          <TextArea
            value={motivo}
            onChange={(e) => {
              setMotivo(e.target.value);
              setErroMotivo("");
            }}
            placeholder={
              modoMotivo === "recusar"
                ? "Explique por que a solicitação está sendo recusada…"
                : "Observação para o histórico (opcional)…"
            }
          />
          <AvisoPerdaProducao etapas={perda} confirmado={perdaConfirmada} onConfirmado={setPerdaConfirmada} />
          {erroMotivo && <InlineError>{erroMotivo}</InlineError>}
          <RowEnd>
            <GhostBtn
              type="button"
              onClick={() => {
                setModoMotivo(null);
                setMotivo("");
                setErroMotivo("");
                setPerda(null);
                setPerdaConfirmada(false);
              }}
            >
              Voltar
            </GhostBtn>
            <AsyncButton variant="danger" silentError onClick={confirmarMotivo}>
              {modoMotivo === "recusar" ? "Confirmar recusa" : "Confirmar cancelamento"}
            </AsyncButton>
          </RowEnd>
        </MotivoBox>
      );
    } else {
      footer = (
        <RowEnd>
          {podeAutorizar && (
            <GhostBtn type="button" onClick={() => setModoMotivo("cancelar")}>
              Cancelar solicitação
            </GhostBtn>
          )}
          {situacao === "ABERTA" && podeAutorizar && (
            <DangerGhostBtn type="button" onClick={() => setModoMotivo("recusar")}>
              Recusar
            </DangerGhostBtn>
          )}
          {situacao === "ABERTA" && podeAutorizar && (
            <PrimaryBtn
              type="button"
              title="Autorizar e classificar os itens (demanda, não conformidade, origem, causa, imputação)"
              onClick={() => setClassificar("autorizar")}
            >
              Autorizar e classificar
            </PrimaryBtn>
          )}
          {(situacao === "AUTORIZADA" || situacao === "EM_ANDAMENTO") &&
            podeAtender && !dado.pronta_em && (
              <AsyncButton
                variant="secondary"
                silentError
                successMessage="Marcada como pronta para retirada."
                onClick={async () => {
                  await marcarProntaSolicitacao(request, familia, id);
                  await aposAcao();
                }}
              >
                Marcar pronta
              </AsyncButton>
            )}
          {(situacao === "AUTORIZADA" || situacao === "EM_ANDAMENTO") && podeAtender && !dado.expedida_em && (
            <PrimaryBtn
              type="button"
              title="Quem leva assina na tela; a retirada completa vira Expedida"
              onClick={() => setRetiradaAberta(true)}
            >
              Registrar retirada
            </PrimaryBtn>
          )}
          {situacao === "AUTORIZADA" && podeAtender && (
            <AsyncButton
              variant="secondary"
              silentError
              successMessage="Atendimento iniciado."
              onClick={async () => {
                await iniciarSolicitacao(request, familia, id);
                await aposAcao();
              }}
            >
              Iniciar atendimento
            </AsyncButton>
          )}
          {/* Atender exige a retirada assinada completa (decisão de 22/09/2026). */}
          {(situacao === "AUTORIZADA" || situacao === "EM_ANDAMENTO") &&
            podeAtender && dado.expedida_em && (
              <AsyncButton
                variant="primary"
                silentError
                successMessage="Solicitação marcada como atendida."
                onClick={async () => {
                  await atenderSolicitacao(request, familia, id);
                  await aposAcao();
                }}
              >
                Marcar como atendida
              </AsyncButton>
            )}
        </RowEnd>
      );
    }
  }

  // Excluir definitivamente fica disponível em QUALQUER situação (inclusive
  // terminais), separado das ações normais. Só quem tem solicitacao:delete.
  const footerCompleto =
    dado && podeExcluir && !modoMotivo ? (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {footer}
        <RowEnd>
          <AsyncButton variant="danger" silentSuccess onClick={excluir}>
            <FiTrash2 size={14} /> Excluir definitivamente
          </AsyncButton>
        </RowEnd>
      </div>
    ) : (
      footer
    );

  return (
    <SlideOverPanel
      open
      onClose={onClose}
      title={dado?.numero || numero || "Solicitação"}
      subtitle={TIPO_LABELS[dado?.tipo] || ""}
      width="min(96vw, max(60vw, 680px))"
      footer={footerCompleto}
    >
      {erro && <ErrorMsg>{erro}</ErrorMsg>}
      {!erro && !dado && <Carregando />}

      {dado && (
        <>
          <DetailTopRow>
            <Pill $tone={SITUACAO_TONES[dado.situacao]}>
              {SITUACAO_LABELS[dado.situacao] || dado.situacao}
            </Pill>
            <Pill $tone="neutral">{dado.tipo}</Pill>
            {(dado.lotes_numeros || []).map((num) => (
              <LoteTag key={num} title={`Peças no lote ${num}`}>
                LOTE {num}
              </LoteTag>
            ))}
          </DetailTopRow>

          <DetailSection>
            <BtnImagens
              type="button"
              onClick={imprimirTodasAsImagens}
              disabled={paginasImagens.length === 0}
              title={
                paginasImagens.length
                  ? `Imprime ${paginasImagens.length} imagem(ns), uma por folha, com cabeçalho`
                  : "Esta solicitação não tem imagens anexadas"
              }
            >
              <FiImage size={15} />
              Imprimir imagens
              {paginasImagens.length > 0 && (
                <span className="qtd">({paginasImagens.length})</span>
              )}
            </BtnImagens>
          </DetailSection>

          {dado.situacao === "RECUSADA" && dado.motivo_recusa && (
            <DetailSection>
              <ErrorMsg>Recusada — {dado.motivo_recusa}</ErrorMsg>
            </DetailSection>
          )}
          {dado.situacao === "CANCELADA" && (
            <DetailSection>
              <NeutralBanner>
                Cancelada{dado.motivo_cancelamento ? ` — ${dado.motivo_cancelamento}` : ""}
              </NeutralBanner>
            </DetailSection>
          )}

          {dado.situacao === "JUNTADA" && (
            <DetailSection>
              <NeutralBanner>
                Juntada na{" "}
                <LinkSolicitacao
                  numero={dado.juntada_na_numero}
                  onAbrir={() => onAbrirOutra?.({ familia, id: dado.juntada_na, numero: dado.juntada_na_numero })}
                />
                {juntadaMeta && ` · ${juntadaMeta}`}. As peças, os itens e a conversa seguem na principal.
              </NeutralBanner>
            </DetailSection>
          )}
          {(dado.juntadas || []).length > 0 && (
            <DetailSection>
              <NeutralBanner>
                Recebeu numa junção:{" "}
                {dado.juntadas.map((j, i) => (
                  <React.Fragment key={j.id}>
                    {i > 0 && ", "}
                    <LinkSolicitacao
                      numero={j.numero}
                      onAbrir={() => onAbrirOutra?.({ familia, id: j.id, numero: j.numero })}
                    />
                  </React.Fragment>
                ))}
                .
              </NeutralBanner>
            </DetailSection>
          )}

          <DetailSection>
            <DetailGrid>
              <DetailItem>
                <div className="lbl">Pedido</div>
                <div className="val">{dado.num_pedido || "—"}</div>
              </DetailItem>
              <DetailItem>
                <div className="lbl">Cliente</div>
                <div className="val">{dado.cliente_nome || "—"}</div>
              </DetailItem>
              <DetailItem>
                <div className="lbl">Supervisor</div>
                <div className="val">{dado.supervisor_nome || "—"}</div>
              </DetailItem>
              <DetailItem>
                <div className="lbl">Líder de montagem</div>
                <div className="val">{dado.lider_montagem_nome || "—"}</div>
              </DetailItem>
              {dado.tipo === "ASP" && (
                <>
                  <DetailItem>
                    <div className="lbl">Setor</div>
                    <div className="val">{dado.setor || "—"}</div>
                  </DetailItem>
                  <DetailItem>
                    <div className="lbl">Lote de produção</div>
                    <div className="val">{dado.lote_producao || "—"}</div>
                  </DetailItem>
                </>
              )}
            </DetailGrid>
          </DetailSection>

          <PrazoPanel
            familia={familia}
            id={id}
            dado={dado}
            podeEditar={podeAutorizar}
            onChanged={aposAcao}
          />

          {(dado.pronta_em || dado.expedida_em || (dado.retiradas || []).length > 0) && (
            <DetailSection>
              <SectionTitle>Expedição</SectionTitle>
              {dado.pronta_em && (
                <PecaLinha>
                  <FiCheckCircle size={13} style={{ verticalAlign: "-2px", marginRight: 4 }} />
                  Pronta para retirada em {dataHora(dado.pronta_em)}
                  {dado.pronta_por_nome ? ` · ${dado.pronta_por_nome}` : ""}
                </PecaLinha>
              )}
              {dado.expedida_em && (
                <PecaLinha>
                  <FiCheckCircle size={13} style={{ verticalAlign: "-2px", marginRight: 4 }} />
                  Expedida (retirada completa) em {dataHora(dado.expedida_em)}
                </PecaLinha>
              )}
              {(dado.retiradas || []).map((r) => (
                <PecaCard key={r.id}>
                  <PecaHead>
                    <strong>Retirada {r.ordem}</strong>
                    <Pill $tone={r.completa ? "success" : "warning"}>
                      {r.completa ? "completa" : "parcial"}
                    </Pill>
                    {r.tem_foto && <Pill $tone="neutral">com foto</Pill>}
                    <GhostBtn
                      type="button"
                      style={{ height: 28, padding: "0 10px", fontSize: 12, marginLeft: "auto" }}
                      title="Reimprimir comprovante com a assinatura"
                      onClick={() => reimprimirRetirada(r)}
                    >
                      <FiPrinter size={13} /> Comprovante
                    </GhostBtn>
                  </PecaHead>
                  <PecaLinha>
                    <span className="forte">{r.retirado_por_nome}</span>
                    {r.retirado_por_documento ? ` · ${r.retirado_por_documento}` : ""}
                    {r.retirado_por_telefone ? ` · ${r.retirado_por_telefone}` : ""}
                  </PecaLinha>
                  <PecaLinha>
                    {dataHora(r.retirado_em)} · entregue por {r.entregue_por_nome || "—"}
                  </PecaLinha>
                  {(r.itens || []).length > 0 && (
                    <PecaLinha>
                      {r.itens.map((it) => `${it.descricao} (${fmtQtd(it.quantidade)})`).join(" · ")}
                    </PecaLinha>
                  )}
                  {r.observacao && <PecaLinha>Obs.: {r.observacao}</PecaLinha>}
                </PecaCard>
              ))}
            </DetailSection>
          )}

          {/* Separação só existe depois da autorização do gestor. */}
          {familia === "requisicao" &&
            podeAtender &&
            situacao !== "ABERTA" &&
            situacao !== "RECUSADA" &&
            situacao !== "CANCELADA" &&
            situacao !== "JUNTADA" && (
              <DetailSection>
                {dado.separado_em && (
                  <PecaLinha style={{ marginBottom: 8 }}>
                    <FiCheckCircle
                      size={13}
                      style={{ verticalAlign: "-2px", marginRight: 4 }}
                    />
                    Separado por {dado.separado_por_nome || "—"} ·{" "}
                    {dataHora(dado.separado_em)}
                  </PecaLinha>
                )}
                <AsyncButton
                  variant="secondary"
                  silentError
                  successMessage={
                    dado.separado_em ? undefined : "Requisição separada."
                  }
                  silentSuccess={!!dado.separado_em}
                  onClick={separarEImprimir}
                  style={{ width: "100%", justifyContent: "center" }}
                >
                  <FiPrinter size={16} style={{ marginRight: 6 }} />
                  {dado.separado_em
                    ? "Reimprimir comprovante de separação"
                    : "Marcar separado + imprimir comprovante"}
                </AsyncButton>
              </DetailSection>
            )}

          {familia === "assistencia" && (dado.pecas || []).length > 0 && (
            <DetailSection>
              <SectionTitle>Simulação das peças</SectionTitle>
              <CarrosselPecasFita pecas={dado.pecas} />
            </DetailSection>
          )}

          {familia === "assistencia" && (
            <DetailSection>
              <SectionTitle style={{ display: "flex", alignItems: "center", gap: 8 }}>
                Peças ({dado.pecas?.length || 0})
                {podeAutorizar && situacao !== "ABERTA" && (
                  <GhostBtn
                    type="button"
                    style={{ height: 28, padding: "0 10px", fontSize: 12, marginLeft: "auto" }}
                    title="Reclassificar a não-qualidade dos itens"
                    onClick={() => setClassificar("classificar")}
                  >
                    <FiEdit2 size={13} /> Classificação
                  </GhostBtn>
                )}
              </SectionTitle>
              {(dado.pecas || []).map((p) => (
                <PecaCard key={p.id}>
                  <PecaHead>
                    <strong>{p.codigo_peca || "Sem código"}</strong>
                    {NATUREZA[p.natureza] && (
                      <Pill $tone={NATUREZA[p.natureza].tone}>
                        {NATUREZA[p.natureza].label}
                      </Pill>
                    )}
                    {p.lote_numero && <Pill $tone="info">Lote {p.lote_numero}</Pill>}
                  </PecaHead>
                  {p.descricao && (
                    <PecaLinha>
                      <span className="forte">{p.descricao}</span>
                    </PecaLinha>
                  )}
                  {(p.ambiente || p.modulo) && (
                    <PecaLinha>
                      {[p.ambiente, p.modulo].filter(Boolean).join(" · ")}
                      {p.ref_modulo ? ` (ref. ${p.ref_modulo})` : ""}
                    </PecaLinha>
                  )}
                  {formatMedidas(p) && (
                    <PecaLinha>
                      {formatMedidas(p)} · Qtd: {p.quantidade ?? 1}
                    </PecaLinha>
                  )}
                  {(p.material_nome || p.material_codigo) && (
                    <PecaLinha>
                      Material: {[p.material_codigo, p.material_nome].filter(Boolean).join(" — ")}
                    </PecaLinha>
                  )}
                  {p.observacao && <PecaLinha>Obs.: {p.observacao}</PecaLinha>}
                  {p.motivo_categoria_display && (
                    <PecaLinha>
                      Motivo: {p.motivo_categoria_display}
                      {p.motivo_detalhe ? ` — ${p.motivo_detalhe}` : ""}
                    </PecaLinha>
                  )}
                  {(p.acabamento || p.usinagem || p.pintura) && (
                    <PecaLinha>
                      {[
                        p.acabamento && `Acabamento: ${p.acabamento}`,
                        // Usinagem/Pintura exibem a descrição (fallback "sim").
                        p.usinagem && `Usinagem: ${p.usinagem_descricao || "sim"}`,
                        p.pintura && `Pintura: ${p.pintura_descricao || "sim"}`,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </PecaLinha>
                  )}
                  <Fitas peca={p} />
                  {p.custo_material_estimado && (
                    <PecaLinha title={p.custo_material_regua || ""}>
                      Custo estimado pela chapa: {formatarReaisBR(p.custo_material_estimado)}
                    </PecaLinha>
                  )}
                  {situacao !== "ABERTA" && <ClassificacaoResumo item={p} />}
                  <GaleriaFotos fotos={p.fotos} peca={p} onAbrir={setFotoAberta} />
                </PecaCard>
              ))}
            </DetailSection>
          )}

          {familia === "requisicao" && (
            <DetailSection>
              <SectionTitle style={{ display: "flex", alignItems: "center", gap: 8 }}>
                Itens ({dado.itens?.length || 0})
                {podeAutorizar && situacao !== "ABERTA" && (
                  <GhostBtn
                    type="button"
                    style={{ height: 28, padding: "0 10px", fontSize: 12, marginLeft: "auto" }}
                    title="Reclassificar a não-qualidade dos itens"
                    onClick={() => setClassificar("classificar")}
                  >
                    <FiEdit2 size={13} /> Classificação
                  </GhostBtn>
                )}
              </SectionTitle>
              {(dado.itens || []).map((it, idx) => (
                <PecaCard key={`${it.codigo || "item"}-${idx}`}>
                  <PecaHead>
                    <strong>{it.codigo || `Item ${idx + 1}`}</strong>
                    <Pill $tone="neutral">
                      {fmtQtd(it.quantidade)} {it.unidade_medida || "UN"}
                    </Pill>
                    {it.produto_codigo && (
                      <Pill $tone="info" title="Item ligado ao Cadastro de Suprimentos">
                        {it.produto_codigo}
                      </Pill>
                    )}
                    {it.separado_em ? (
                      <Pill $tone={it.moveu_estoque ? "success" : "neutral"}>
                        {it.moveu_estoque ? `baixado ${fmtQtd(it.quantidade_separada)}` : "separado"}
                      </Pill>
                    ) : (
                      podeAtender && it.id && (situacao === "AUTORIZADA" || situacao === "EM_ANDAMENTO") && (
                        <AsyncButton
                          variant="secondary"
                          style={{ padding: "2px 8px", fontSize: 12 }}
                          successMessage={it.produto ? "Item separado e baixado do estoque." : "Item separado."}
                          onClick={async () => {
                            await separarItemSolicitacao(request, id, it.id);
                            await carregar();
                            onChanged?.();
                          }}
                        >
                          Separar
                        </AsyncButton>
                      )
                    )}
                  </PecaHead>
                  {it.descricao && (
                    <PecaLinha>
                      <span className="forte">{it.descricao}</span>
                    </PecaLinha>
                  )}
                  {it.ambiente && <PecaLinha>Ambiente: {it.ambiente}</PecaLinha>}
                  {it.medida && <PecaLinha>Medida: {it.medida}</PecaLinha>}
                  {it.motivo && (
                    <PecaLinha>
                      Motivo: {it.motivo_display || it.motivo}
                      {it.motivo_detalhe ? ` — ${it.motivo_detalhe}` : ""}
                    </PecaLinha>
                  )}
                  {situacao !== "ABERTA" && <ClassificacaoResumo item={it} />}
                </PecaCard>
              ))}
              <GaleriaFotos fotos={dado.fotos} onAbrir={setFotoAberta} />
            </DetailSection>
          )}

          <DetailSection>
            <SectionTitle>Histórico</SectionTitle>
            <Timeline>
              <TimelineItem $tone="neutral">
                <div className="titulo">Criada</div>
                <div className="meta">
                  {dado.criado_por_nome || "—"} · {dataHora(dado.created_at)}
                </div>
              </TimelineItem>
              {dado.autorizada_em && (
                <TimelineItem $tone="info">
                  <div className="titulo">Autorizada</div>
                  <div className="meta">{dataHora(dado.autorizada_em)}</div>
                </TimelineItem>
              )}
              {dado.em_andamento_em && (
                <TimelineItem $tone="info">
                  <div className="titulo">Em andamento</div>
                  <div className="meta">
                    {[dado.iniciado_por_nome, dataHora(dado.em_andamento_em)]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </TimelineItem>
              )}
              {dado.pronta_em && (
                <TimelineItem $tone="info">
                  <div className="titulo">Pronta para retirada</div>
                  <div className="meta">
                    {[dado.pronta_por_nome, dataHora(dado.pronta_em)].filter(Boolean).join(" · ")}
                  </div>
                </TimelineItem>
              )}
              {dado.expedida_em && (
                <TimelineItem $tone="success">
                  <div className="titulo">Expedida</div>
                  <div className="meta">Retirada completa · {dataHora(dado.expedida_em)}</div>
                </TimelineItem>
              )}
              {dado.atendida_em && (
                <TimelineItem $tone="success">
                  <div className="titulo">Atendida</div>
                  <div className="meta">{dataHora(dado.atendida_em)}</div>
                </TimelineItem>
              )}
              {dado.situacao === "RECUSADA" && (
                <TimelineItem $tone="danger">
                  <div className="titulo">Recusada</div>
                  <div className="meta">
                    {[dado.recusado_por_nome, dado.recusada_em && dataHora(dado.recusada_em)].filter(Boolean).join(" · ")}
                  </div>
                  <div className="meta">{dado.motivo_recusa || "Sem motivo registrado"}</div>
                </TimelineItem>
              )}
              {dado.situacao === "JUNTADA" && (
                <TimelineItem $tone="neutral">
                  <div className="titulo">Juntada na {dado.juntada_na_numero}</div>
                  <div className="meta">{juntadaMeta}</div>
                </TimelineItem>
              )}
              {dado.situacao === "CANCELADA" && (
                <TimelineItem $tone="neutral">
                  <div className="titulo">Cancelada</div>
                  <div className="meta">
                    {[dado.cancelado_por_nome, dado.cancelada_em && dataHora(dado.cancelada_em)].filter(Boolean).join(" · ")}
                  </div>
                  <div className="meta">
                    {dado.motivo_cancelamento || "Sem motivo registrado"}
                  </div>
                </TimelineItem>
              )}
            </Timeline>
          </DetailSection>

          {dado.reservas_presas > 0 && podeAtender && (
            <WarningBanner style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
              <span style={{ flex: 1, minWidth: 220 }}>
                {dado.reservas_presas} reserva(s) de estoque ainda presa(s) a esta requisição, que já não
                precisa delas. Enquanto presas, o almoxarifado vê menos material disponível do que tem.
              </span>
              <AsyncButton variant="secondary" successMessage="Reservas soltas."
                           onClick={async () => { await soltarReservasPresas(request, familia, id); await aposAcao(); }}>
                Soltar reservas
              </AsyncButton>
            </WarningBanner>
          )}

          {/* A conversa foi junto para a principal. */}
          {dado.situacao !== "JUNTADA" && <Conversa familia={familia} id={id} />}
        </>
      )}

      {classificar && (
        <AutorizarClassificarModal
          familia={familia}
          id={id}
          numero={dado?.numero || numero}
          modo={classificar}
          onClose={() => setClassificar(null)}
          onDone={async () => {
            setClassificar(null);
            await aposAcao();
          }}
        />
      )}

      <ConfirmDialog
        open={confirmandoExclusao}
        titulo={`Excluir definitivamente ${numero}?`}
        descricao={'Apaga a solicitação e suas peças, itens e fotos, sem volta. Para só encerrá-la e manter o histórico, use "Cancelar solicitação".'}
        confirmarLabel="Excluir definitivamente"
        tone="danger"
        onConfirmar={excluirDeVez}
        onCancelar={() => setConfirmandoExclusao(false)}
      />

      {retiradaAberta && (
        <RetiradaModal
          familia={familia}
          id={id}
          numero={dado?.numero || numero}
          detalhe={dado}
          onClose={() => setRetiradaAberta(false)}
          onDone={async () => {
            setRetiradaAberta(false);
            await aposAcao();
          }}
        />
      )}

      {fotoAberta && (
        <ImagemViewer
          foto={fotoAberta.foto}
          cabecalho={cabecalhoDaFoto(dado, fotoAberta.peca)}
          onFechar={() => setFotoAberta(null)}
          onSalvar={salvarFoto}
          onRestaurar={restaurarFoto}
        />
      )}
    </SlideOverPanel>
  );
}
